import { useTranslation } from 'react-i18next';
import Dinero from 'dinero.js';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  ArticlePackageQuery,
  CampaignQuery,
  DeliveryMethod,
  FormFieldEnum,
  useArticlePackageQuery,
} from '../../generated/graphql';
import useQueryParams from './queryParam.hook';

const useDemoCampaign = () : {
  loading: boolean,
  campaign?: CampaignQuery['campaignBySlug'] & { articlePackage: ArticlePackageQuery['articlePackage'] },
} => {
  const articlePackageId = useQueryParams('articlePackageId');
  const [translate, i18n] = useTranslation();
  const [search] = useSearchParams();
  const queryParamLocale = search.get('lang');
  const { slug } = useParams();

  const getLocale = (articlePackage: ArticlePackageQuery['articlePackage']) => queryParamLocale
        || ((articlePackage || {}).locales || [])[0] || ((articlePackage || {}).partner || {}).locale
        || 'nl-BE';

  const {
    loading,
    data: {
      articlePackage,
    } = {},
  } = useArticlePackageQuery({
    skip: !articlePackageId,
    variables: { _id: articlePackageId || '' },
    onCompleted({ articlePackage: ap } = {}) {
      const targetLocale = getLocale(ap);
      if (targetLocale) {
        i18n.changeLanguage(targetLocale);
        Dinero.globalLocale = targetLocale;
      }
    },
  });

  if (slug !== 'demo') return { loading: false, campaign: undefined };

  const { articleTemplates = [], defaultCampaignSettings } = articlePackage || {};
  const {
    headerImgUrl: defaultHeaderImgUrl,
    deliveryOptions: defaultDeliveryOptions,
    minimumTotalArticleCount: defaultMinimumTotalArticleCount,
    minimumOrderValue: defaultMinimumOrderValue,
  } = defaultCampaignSettings || {};

  const campaign = {
    slug: 'demo',
    name: `Demo Webshop ${articlePackage?.name || ''}`,
    headerImgUrl: defaultHeaderImgUrl,
    articlePackage,
    textConfig: {
      deliveryLabel: null,
      emailLabel: null,
      emailDescription: null,
      nameLabel: null,
      nameDescription: null,
      sellerGroupLabel: null,
      sellerGroupDescription: null,
      sellerGroupPlaceholder: null,
      addressLabel: null,
      info: `<p>${translate('COMPONENTS.DEMO.TEXT_CONFIG.INFO')}</p>`,
      scoreboard: `<p>${translate('COMPONENTS.DEMO.TEXT_CONFIG.SCOREBOARD')}</p>`,
      sellingDisabled: `<p>${translate('COMPONENTS.DEMO.TEXT_CONFIG.SELLING_DISABLED')}</p>`,
      variantUnavailable: null,
      delivery: '',
      __typename: 'TextConfig' as const,
    },
    openGraphTags: {
      title: translate('COMPONENTS.DEMO.OPEN_GRAPH_TAGS.TITLE'),
      description: translate('COMPONENTS.DEMO.OPEN_GRAPH_TAGS.DESCRIPTION'),
      // eslint-disable-next-line max-len
      imageUrl: 'https://res.cloudinary.com/crowdselling/image/upload/v1613521635/campaigns/5eaf032d139d2e4855084f07/tenant-5e94162be80c7b004c8785ee-3346397318273173-wijnverkoop-demo-banner.png',
      __typename: 'OpenGraphTags' as const,
    },
    sellerGroups: [],
    articles: [...articleTemplates.map((t) => ({
      _id: t._id,
      name: t.name,
      price: t.sellingPrice,
      imageUrls: t.imageUrls,
      description: t.description,
      extraInfo: t.extraInfo,
      groupId: t.groupId,
      features: t.features,
      itemsLeft: null,
      heading: null,
      __typename: 'Article' as const,
    })),
    {
      _id: 'donation',
      name: translate('COMMON.DONATION'),
      price: 1,
      __typename: 'DonationArticle' as const,
    },
    {
      _id: 'shipping',
      name: translate('COMMON.SHIPPING_COST'),
      price: 750,
      __typename: 'ShippingCostsArticle' as const,
    }],
    minimumTotalArticleCount: defaultMinimumTotalArticleCount,
    minimumOrderValue: defaultMinimumOrderValue,
    deliveryOptions: {
      ...defaultDeliveryOptions,
      deliveryMethods: [DeliveryMethod.Deliver, DeliveryMethod.Collect],
      deliveryCountries: [],
      deliveryRadii: [{
        _id: 'deliveryRadius',
        centralAddress: {
          description: 'Korenmarkt, 9000 Gent, Belgium',
          coordinates: [3.7217769, 51.0546034],
          __typename: 'Address' as const,
        },
        radius: 10000,
        __typename: 'DeliveryRadius' as const,
      }],
      minimumTotalPrice: 0,
      __typename: 'DeliveryOptions' as const,
    },
    organisation: {
      locale: getLocale(articlePackage),
      __typename: 'Organisation' as const,
    },
    formFields: [{
      _id: '611101ba98eac9000823db42',
      label: 'Telefoonnummer',
      description: 'Zo kunnen we je contacteren als er iets aan je bestelling verandert.',
      placeholder: 'Bv. 0473 00 00 00',
      isRequired: true,
      isMultiselect: false,
      isDisabled: null,
      options: [],
      type: FormFieldEnum.FreeText,
      __typename: 'FormField' as const,
    }],
    timeslots: {
      collect: [],
      deliver: [],
      eatIn: [],
      __typename: 'Timeslots' as const,
    },
    isSellingEnabled: true,
    isSellerGroupRequired: null,
    showScoreboard: true,
    winnerCount: 3,
    acceptDonations: true,
    interfaceConfig: {
      sortFeatures: null,
      hideArticleCounter: null,
      __typename: 'InterfaceConfig' as const,
    },
    onlyFinalOrderAllowed: false,
    __typename: 'Campaign' as const,
  };

  return {
    loading,
    campaign,
  };
};

export default useDemoCampaign;
